import * as React from "react";
import { api, postJson } from "./api";
import { AppLink, useNavigation } from "./router";
import type { AppUser } from "./room-session";

interface PublicRoom {
  id: string;
  alias: string;
  title: string;
  listenerCount: number;
  nowPlaying: null | { title: string; artist: string; artworkUrl: string | null };
}

interface HouseStation {
  slug: string;
  title: string;
  description: string;
  accent: "violet" | "orange" | "cyan";
  state: "ready" | "live";
  listenerCount: number;
  roomRef: string | null;
  nowPlaying: null | { title: string; artist: string; artworkUrl: string | null };
}

export function HomePage({ user, onRequireAuth }: { user: AppUser | null; onRequireAuth: () => void }) {
  const { navigate } = useNavigation();
  const [rooms, setRooms] = React.useState<PublicRoom[]>([]);
  const [stations, setStations] = React.useState<HouseStation[]>([]);
  const [stationPlaybackEnabled, setStationPlaybackEnabled] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState("");

  const loadRooms = React.useCallback(() => {
    setLoading(true);
    void Promise.all([
      api<{ rooms: PublicRoom[] }>("/api/rooms"),
      api<{ stations: HouseStation[]; playbackEnabled: boolean }>("/api/house-stations"),
    ])
      .then(([roomPayload, stationPayload]) => {
        setRooms(roomPayload.rooms);
        setStations(stationPayload.stations);
        setStationPlaybackEnabled(stationPayload.playbackEnabled);
      })
      .catch((cause) => setError(cause?.message || "Could not tune the room dial"))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(loadRooms, [loadRooms]);

  const stumble = async () => {
    setError("");
    try {
      const payload = await postJson<{ room: { alias?: string; id: string } }>("/api/matchmake", {});
      navigate(`/rooms/${encodeURIComponent(payload.room.alias || payload.room.id)}`);
    } catch (cause: any) {
      setError(cause?.message || "No rooms are live yet");
    }
  };

  const enterStation = async (station: HouseStation) => {
    if (!user) return onRequireAuth();
    setError("");
    try {
      const payload = await postJson<{ room: { alias: string } }>(`/api/house-stations/${encodeURIComponent(station.slug)}/enter`, {});
      navigate(`/rooms/${encodeURIComponent(payload.room.alias)}`);
    } catch (cause: any) {
      setError(cause?.message || "Could not open that House Station");
    }
  };

  const createRoom = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) return onRequireAuth();
    const form = new FormData(event.currentTarget);
    setCreating(true);
    setError("");
    try {
      const payload = await postJson<{ room: { alias: string } }>("/api/rooms", {
        title: String(form.get("title") || ""),
        visibility: String(form.get("visibility") || "public"),
        mode: String(form.get("mode") || "hybrid"),
        is_discoverable: true,
      });
      navigate(`/rooms/${encodeURIComponent(payload.room.alias)}`);
    } catch (cause: any) {
      setError(cause?.message || "Could not start the room");
    } finally {
      setCreating(false);
    }
  };

  return (
    <main className="home-page page-shell">
      <section className="home-hero">
        <div className="hero-copy">
          <span className="eyebrow live-eyebrow"><i /> LIVE SOCIAL RADIO</span>
          <h1>Stumble into<br /><em>someone else’s song.</em></h1>
          <p>Drop through live rooms, trade exact song Fragments, and follow the path a recommendation takes between people.</p>
          <div className="hero-actions">
            <button className="button primary jumbo" onClick={() => void stumble()}>Stumble into a room <span>→</span></button>
            {!user && <button className="button ghost jumbo" onClick={onRequireAuth}>Connect Spotify</button>}
          </div>
          <div className="hero-proof"><span>{rooms.length}</span> public room{rooms.length === 1 ? "" : "s"} on the dial</div>
        </div>
        <div className="signal-orbit" aria-hidden="true">
          <div className="orbit orbit-one" /><div className="orbit orbit-two" /><div className="orbit orbit-three" />
          <div className="orbit-center">JB</div>
          {rooms.slice(0, 4).map((room, index) => <div className={`orbit-node node-${index + 1}`} key={room.id}>{room.nowPlaying?.artworkUrl ? <img src={room.nowPlaying.artworkUrl} alt="" /> : "♪"}</div>)}
        </div>
      </section>

      {error && <div className="notice error-notice" role="status">{error}</div>}

      <section className="house-stations-section">
        <header className="section-heading house-stations-heading">
          <div><span className="eyebrow">JAMBAND HOUSE STATIONS</span><h2>There is always somewhere to start.</h2></div>
          <p>Permanent rooms with a house rotation until listeners take over the queue.</p>
        </header>
        <div className="house-station-grid">
          {stations.map((station) => (
            <article className={`house-station-card station-${station.accent}`} key={station.slug}>
              <div className="house-station-art">
                {station.nowPlaying?.artworkUrl ? <img src={station.nowPlaying.artworkUrl} alt="" /> : <span>JB</span>}
                <i className={station.state === "live" ? "live" : ""} />
              </div>
              <div className="house-station-copy">
                <span className={`station-state ${station.state}`}>
                  {station.state === "live" ? `LIVE · ${station.listenerCount} LISTENER${station.listenerCount === 1 ? "" : "S"}` : "READY TO START"}
                </span>
                <h3>{station.title}</h3>
                <p>{station.nowPlaying ? `${station.nowPlaying.title} · ${station.nowPlaying.artist}` : station.description}</p>
              </div>
              <button className="button station-enter" onClick={() => void enterStation(station)} disabled={!stationPlaybackEnabled}>
                {!stationPlaybackEnabled ? "Playback paused" : user ? station.state === "live" ? "Join station →" : "Start station →" : "Connect to enter →"}
              </button>
            </article>
          ))}
        </div>
        {!stationPlaybackEnabled && <p className="beta-access-note">House Station playback is temporarily disabled. The public dial remains available.</p>}
      </section>

      <section className="home-grid">
        <div className="room-dial panel">
          <header className="section-heading"><div><span className="eyebrow">ON AIR NOW</span><h2>Rooms to stumble into</h2></div><button className="text-button" onClick={loadRooms}>Refresh</button></header>
          {loading ? <div className="loading-line">Scanning the dial…</div> : rooms.length === 0 ? (
            <div className="empty-state"><span>◌</span><h3>The dial is quiet</h3><p>Start the first room and put something on.</p></div>
          ) : (
            <div className="room-list">
              {rooms.map((room) => (
                <AppLink className="room-row" href={`/rooms/${encodeURIComponent(room.alias)}`} key={room.id}>
                  <div className="track-art mini-art">{room.nowPlaying?.artworkUrl ? <img src={room.nowPlaying.artworkUrl} width="52" height="52" alt="" /> : <span>♪</span>}</div>
                  <div><strong>{room.title}</strong><small>{room.nowPlaying ? `${room.nowPlaying.title} · ${room.nowPlaying.artist}` : "Waiting for the first song"}</small></div>
                  <span className="listener-pill"><i /> {room.listenerCount}</span><span className="row-arrow">→</span>
                </AppLink>
              ))}
            </div>
          )}
        </div>

        <form className="start-room panel" onSubmit={createRoom}>
          <span className="eyebrow">YOUR FREQUENCY</span><h2>Start a room</h2><p>Pick a name. You become host; everyone brings their own Spotify playback.</p>
          <label><span>Room title</span><input name="title" maxLength={80} placeholder={user ? `${user.displayName} after dark` : "Connect Spotify first"} disabled={!user} /></label>
          <div className="form-pair">
            <label><span>Visibility</span><select name="visibility" disabled={!user}><option value="public">Public</option><option value="unlisted">Unlisted</option><option value="private">Private</option></select></label>
            <label><span>Queue</span><select name="mode" disabled={!user}><option value="hybrid">Everyone adds</option><option value="host_only">Host only</option><option value="democracy">Vote-led</option></select></label>
          </div>
          <button className="button primary full" type={user ? "submit" : "button"} onClick={!user ? onRequireAuth : undefined} disabled={creating}>{creating ? "Opening room…" : user ? "Start broadcasting" : "Connect Spotify to host"}</button>
        </form>
      </section>
    </main>
  );
}
